import { useState, useEffect } from "react";
import { useParams } from "react-router";
import {
  Loader2, AlertTriangle, Store, ClipboardList,
  DollarSign, Calendar,
} from "lucide-react";

interface VendorData {
  id: string;
  name: string;
  payment_terms: string;
  total_ordered: number;
  total_paid: number;
  balance_owed: number;
}

interface POData {
  id: string;
  po_number: string;
  status: string;
  description: string;
  total: number;
  created_at: string;
}

const SUPABASE_URL = import.meta.env.VITE_SUPABASE_URL || "";

function fmt(n: number): string {
  return new Intl.NumberFormat("en-US", { style: "currency", currency: "USD" }).format(n);
}

function shortDate(iso: string): string {
  return new Date(iso).toLocaleDateString("en-US", { month: "short", day: "numeric" });
}

const PO_COLORS: Record<string, { color: string; bg: string }> = {
  draft: { color: "#6b7280", bg: "#f3f4f6" },
  submitted: { color: "#2563eb", bg: "#dbeafe" },
  received: { color: "#d97706", bg: "#fef3c7" },
  paid: { color: "#16a34a", bg: "#dcfce7" },
  cancelled: { color: "#dc2626", bg: "#fee2e2" },
};

const TERMS_LABELS: Record<string, string> = {
  cod: "COD", net_15: "Net 15", net_30: "Net 30", net_45: "Net 45", net_60: "Net 60",
};

/**
 * Public vendor portal — token-based, no auth.
 * Uses direct Supabase REST queries with anon key (RLS bypassed via service role
 * would be needed; for now this serves as the UI shell that can be powered by
 * a dedicated Edge Function similar to customer-portal).
 *
 * For simplicity, we query the vendor + POs directly via the anon key.
 * In production, create a vendor-portal Edge Function.
 */
export function VendorPortalPage() {
  const { token } = useParams<{ token: string }>();
  const [vendor, setVendor] = useState<VendorData | null>(null);
  const [pos, setPOs] = useState<POData[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (!token) {
      setError("Invalid link");
      setLoading(false);
      return;
    }

    const ANON_KEY = import.meta.env.VITE_SUPABASE_ANON_KEY || "";

    // Fetch vendor by portal_token
    fetch(
      `${SUPABASE_URL}/rest/v1/vendors?portal_token=eq.${token}&select=id,name,payment_terms,total_ordered,total_paid,balance_owed`,
      {
        headers: {
          apikey: ANON_KEY,
          Authorization: `Bearer ${ANON_KEY}`,
        },
      }
    )
      .then(async (res) => {
        if (!res.ok) throw new Error("Vendor not found");
        const data = await res.json();
        if (!data || data.length === 0) throw new Error("Vendor not found");
        const v = data[0];
        setVendor(v);

        // Fetch POs for this vendor
        return fetch(
          `${SUPABASE_URL}/rest/v1/purchase_orders?vendor_id=eq.${v.id}&select=id,po_number,status,description,total,created_at&order=created_at.desc`,
          {
            headers: {
              apikey: ANON_KEY,
              Authorization: `Bearer ${ANON_KEY}`,
            },
          }
        );
      })
      .then(async (res) => {
        if (res && res.ok) {
          const data = await res.json();
          setPOs(data || []);
        }
      })
      .catch((err) => setError(err.message))
      .finally(() => setLoading(false));
  }, [token]);

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50">
        <Loader2 className="w-8 h-8 animate-spin text-blue-600" />
      </div>
    );
  }

  if (error || !vendor) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50 p-4">
        <div className="text-center">
          <AlertTriangle className="w-12 h-12 text-amber-500 mx-auto mb-3" />
          <h1 className="text-xl font-bold mb-2">Portal Not Found</h1>
          <p className="text-sm text-gray-500">{error || "Invalid link."}</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50">
      <div className="bg-white border-b sticky top-0 z-10">
        <div className="max-w-2xl mx-auto px-4 py-4 flex items-center gap-3">
          <div className="flex items-center justify-center w-10 h-10 rounded-xl bg-blue-50 shrink-0">
            <Store className="w-5 h-5 text-blue-600" />
          </div>
          <div>
            <p className="font-semibold">{vendor.name}</p>
            <p className="text-xs text-gray-500">
              Vendor Portal · {TERMS_LABELS[vendor.payment_terms] || vendor.payment_terms}
            </p>
          </div>
        </div>
      </div>

      <div className="max-w-2xl mx-auto px-4 py-6 space-y-4">
        {/* Balance summary */}
        <div className="grid grid-cols-3 gap-3">
          <div className="bg-white rounded-xl border p-4 text-center">
            <p className="text-2xl font-bold text-blue-600 tabular-nums">
              {fmt(Number(vendor.total_ordered))}
            </p>
            <p className="text-xs text-muted-foreground mt-0.5">Ordered</p>
          </div>
          <div className="bg-white rounded-xl border p-4 text-center">
            <p className="text-2xl font-bold text-green-600 tabular-nums">
              {fmt(Number(vendor.total_paid))}
            </p>
            <p className="text-xs text-muted-foreground mt-0.5">Paid</p>
          </div>
          <div className="bg-white rounded-xl border p-4 text-center">
            <p className="text-2xl font-bold tabular-nums" style={{
              color: Number(vendor.balance_owed) > 0 ? "#d97706" : "#16a34a",
            }}>
              {fmt(Number(vendor.balance_owed))}
            </p>
            <p className="text-xs text-muted-foreground mt-0.5">Balance</p>
          </div>
        </div>

        {/* PO list */}
        <div className="bg-white rounded-xl border overflow-hidden">
          <div className="px-5 py-3.5 border-b bg-gray-50 flex items-center gap-2">
            <ClipboardList className="w-4 h-4 text-muted-foreground" />
            <span className="text-sm font-semibold">Purchase Orders</span>
            <span className="text-xs text-muted-foreground">({pos.length})</span>
          </div>
          {pos.length === 0 && (
            <p className="px-5 py-6 text-sm text-muted-foreground text-center">
              No purchase orders yet.
            </p>
          )}
          <div className="divide-y">
            {pos.map((po) => {
              const sc = PO_COLORS[po.status] || PO_COLORS.draft;
              return (
                <div key={po.id} className="px-5 py-3 flex items-center justify-between">
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 mb-0.5">
                      <span className="text-xs font-mono text-muted-foreground">{po.po_number}</span>
                      <span className="text-xs px-1.5 py-0.5 rounded font-medium"
                        style={{ color: sc.color, backgroundColor: sc.bg }}>
                        {po.status}
                      </span>
                    </div>
                    <p className="text-sm font-medium truncate">{po.description || "Purchase"}</p>
                    <p className="text-xs text-muted-foreground flex items-center gap-1">
                      <Calendar className="w-3 h-3" /> {shortDate(po.created_at)}
                    </p>
                  </div>
                  <p className="text-sm font-bold tabular-nums shrink-0 ml-3">
                    {fmt(Number(po.total))}
                  </p>
                </div>
              );
            })}
          </div>
        </div>

        <div className="text-center text-xs text-muted-foreground pt-4 pb-8">
          Powered by BldrOS
        </div>
      </div>
    </div>
  );
}

VendorPortalPage.path = "/vendor-portal/:token";
